import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { apiGetActurisConfigStatus, apiResyncBrokerActurisConfig } from 'api';
import {
  type ActurisSyncStatus,
  type ActurisSyncState,
  shouldPollSyncStatus,
} from 'hooks/acturis';

export interface ActurisConfigStatusResponse {
  sync_status: ActurisSyncStatus;
  sync_error?: string | null;
  missing_fields?: string[];
  message: string | null;
}

export type { ActurisSyncStatus };

export const useActurisConfigStatus = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } =
    useQuery<ActurisConfigStatusResponse>({
      queryKey: ['acturis-config-status'],
      queryFn: apiGetActurisConfigStatus,
      staleTime: 1000 * 60 * 5,
      refetchInterval: (query) => {
        const status = query.state.data?.sync_status;
        return shouldPollSyncStatus(status, true) ? 2000 : false;
      },
    });

  const resync = useCallback(async () => {
    await apiResyncBrokerActurisConfig();
    queryClient.setQueryData<ActurisConfigStatusResponse>(
      ['acturis-config-status'],
      (old) =>
        old ? { ...old, sync_status: 'syncing', sync_error: null } : old,
    );
  }, [queryClient]);

  const syncStatus = data?.sync_status ?? null;
  const canAccess = syncStatus === 'ready';
  const missingFields = data?.missing_fields ?? [];

  const syncState: ActurisSyncState = {
    syncStatus,
    syncError: data?.sync_error ?? null,
    hasUsername: syncStatus !== 'not_configured',
    isConfigComplete: canAccess,
    missingFields,
    canAccess,
  };

  return {
    ...syncState,
    message: data?.message ?? null,
    isLoading,
    error,
    refetch,
    resync,
  };
};

export type { ActurisConfigStatusResponse as ActurisConfigStatus };
